import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { SelectOption } from 'src/shared/components/select/Select';
// Input no longer used; CommandInput provides the search field
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from 'src/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'src/shared/components/ui/command';
import { cn } from 'src/shared/helpers/utils';
import styles from './Autocomplete.module.scss';

/**
 * Represents an option that can be used in the Autocomplete component.
 * Can be either a simple string or a SelectOption object with more complex properties.
 */
export type AutocompleteOption = string | SelectOption;

/**
 * Props for the Autocomplete component.
 *
 * @example
 * ```tsx
 * <Autocomplete
 *   options={['Apple', 'Banana', 'Cherry']}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   placeholder="Select a fruit"
 * />
 * ```
 */
export type AutocompleteProps = {
    /** Array of options to display in the dropdown. Can be strings or SelectOption objects. */
    options: AutocompleteOption[];
    /** Callback fired when the selection changes. Receives the selected value or null if cleared. */
    onChange: (value: string | null) => void;
    /** Optional callback fired when the input text changes. Useful for controlled input behavior. */
    onInputChange?: (value: string) => void;
    /** The currently selected value. Must match one of the option values. */
    value: string;
    /** Placeholder text displayed when the input is empty. */
    placeholder?: string;
    /** Width of the component. Can be a CSS width value or number. */
    width?: string | number;
    /** Optional icon to display at the start of the input field. */
    prefixIcon?: JSX.Element;
    /** Whether to disable the clear button functionality. */
    disableClearable?: boolean;
    /** Controlled input value. When provided, controls the text in the input field. */
    inputValue?: string | null;
    /** Custom CSS styles to apply to the option items in the dropdown. */
    customStyle?: React.CSSProperties;
    /** Whether the component is disabled and non-interactive. */
    disabled?: boolean;
    /** Whether to update the input text when an option is selected. Defaults to true. */
    updateInputOnSelect?: boolean;
    /** When true, opening the dropdown shows all options until the user types. Defaults to false. */
    showAllOnOpen?: boolean;
    /** When true, skips client-side filtering and shows all provided options as-is (use for server-side search). Defaults to false. */
    disableClientFilter?: boolean;
};

/**
 * Autocomplete component that provides intelligent text input with suggestions.
 *
 * This component is built using shadcn/ui components (Command, Popover) and provides:
 * - Support for both string arrays and complex option objects
 * - Automatic deduplication of options
 * - Flexible input control with separate state management
 * - Customizable styling and behavior
 * - Full accessibility support
 * - Keyboard navigation support
 * - Option to show all options on open (showAllOnOpen prop)
 *
 * @param props - The component props
 * @returns A React component that renders an autocomplete input field
 *
 * @example
 * ```tsx
 * // Simple string options
 * <Autocomplete
 *   options={['Apple', 'Banana', 'Cherry']}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   placeholder="Select a fruit"
 * />
 *
 * // Complex option objects
 * <Autocomplete
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'uk', label: 'United Kingdom' }
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   placeholder="Select a country"
 * />
 *
 * // With show all options on open
 * <Autocomplete
 *   options={['Apple', 'Banana', 'Cherry']}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   placeholder="Select a fruit"
 *   showAllOnOpen={true}
 * />
 * ```
 */
const Autocomplete = (props: AutocompleteProps) => {
    const {
        placeholder,
        value,
        width = '100%',
        options,
        prefixIcon,
        disableClearable = true,
        onChange,
        onInputChange,
        inputValue,
        customStyle,
        disabled = false,
        updateInputOnSelect = true,
        showAllOnOpen = false,
        disableClientFilter = false,
    } = props;

    // Normalize options to SelectOption for consistent handling
    const normalizedOptions: SelectOption[] = useMemo(() => {
        return options.map(option => {
            if (typeof option === 'string') {
                return { value: option, label: option };
            }
            return option;
        });
    }, [options]);

    // Remove duplicate options by value
    const deduplicatedOptions: SelectOption[] = useMemo(() => {
        return normalizedOptions.filter((option, index, self) => {
            const firstIndexWithValue = self.findIndex(o => o.value === option.value);
            if (firstIndexWithValue === index) {
                return true;
            }
            return false;
        });
    }, [normalizedOptions]);

    const [open, setOpen] = useState(false);
    const [hasUserTypedSinceOpen, setHasUserTypedSinceOpen] = useState(false);

    const inputElRef = useRef<HTMLInputElement | null>(null);
    const commandListRef = useRef<HTMLDivElement | null>(null);

    // Derive selected option
    const selectedOption = useMemo(() => {
        const found = deduplicatedOptions.find(o => o.value === value) || null;
        return found;
    }, [deduplicatedOptions, value]);

    const inputDisplayValue = inputValue || '';

    const shouldShowAll = open && showAllOnOpen && !hasUserTypedSinceOpen;

    const filteredOptions: SelectOption[] = useMemo(() => {
        if (disableClientFilter || shouldShowAll) {
            return deduplicatedOptions;
        }

        const query = inputDisplayValue.toLowerCase();
        if (query.trim().length === 0) {
            return deduplicatedOptions;
        }

        return deduplicatedOptions.filter(opt => {
            const label = String(opt.label ?? opt.value).toLowerCase();
            if (label.includes(query)) {
                return true;
            }
            return false;
        });
    }, [deduplicatedOptions, inputDisplayValue, shouldShowAll, disableClientFilter]);

    // Close dropdown when disabled
    useEffect(() => {
        if (disabled && open) {
            setOpen(false);
        }
    }, [disabled, open]);

    const _handleInputFocus = () => {
        if (!disabled) {
            setOpen(true);
            setHasUserTypedSinceOpen(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setHasUserTypedSinceOpen(true);
        if (onInputChange) {
            onInputChange(next);
        }
        if (!open) {
            setOpen(true);
        }
    };

    // Ensure the currently selected item is visible when opening the dropdown
    useEffect(() => {
        if (!open) {
            return;
        }

        const scrollToSelected = () => {
            const container = (commandListRef.current || document.querySelector('[cmdk-list]')) as HTMLElement | null;
            if (!container) {
                return;
            }

            const scrollElementIntoView = (target: HTMLElement) => {
                const canScroll = typeof (target as any).scrollIntoView === 'function';
                if (canScroll) {
                    (target as any).scrollIntoView({ block: 'nearest' });
                    return;
                }

                const elementTop = target.offsetTop;
                const elementBottom = elementTop + target.offsetHeight;
                const viewTop = container.scrollTop;
                const viewBottom = viewTop + container.clientHeight;

                if (elementTop < viewTop) {
                    container.scrollTop = elementTop;
                } else if (elementBottom > viewBottom) {
                    container.scrollTop = elementBottom - container.clientHeight;
                }
            };

            // Our primary target: item with our explicit data-option-value
            if (selectedOption?.value) {
                const byOptionValue = container.querySelector(
                    `[cmdk-item][data-option-value="${String(selectedOption.value)}"]`
                ) as HTMLElement | null;
                if (byOptionValue) {
                    scrollElementIntoView(byOptionValue);
                    return;
                }

                // Last resort: try cmdk's data-value, case-insensitive compare
                const items = container.querySelectorAll('[cmdk-item][data-value]');
                const targetValue = String(selectedOption.value).toLowerCase();
                for (const el of Array.from(items)) {
                    const element = el as HTMLElement;
                    const dv = (element.getAttribute('data-value') || '').toLowerCase();
                    if (dv === targetValue) {
                        scrollElementIntoView(element);
                        break;
                    }
                }
            }
        };

        // Defer until after CommandList has rendered; retry a few frames to be robust
        let attempts = 0;
        let rafId = 0 as unknown as number;
        const tick = () => {
            attempts += 1;
            scrollToSelected();
            if (attempts < 5 && open) {
                rafId = requestAnimationFrame(tick);
            }
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [open, selectedOption?.value]);

    const handleSelect = (opt: SelectOption) => {
        const selectedValue = opt?.value ?? null;
        onChange(selectedValue);

        if (updateInputOnSelect && onInputChange) {
            const labelString = String(opt.label ?? opt.value);
            onInputChange(labelString);
        }

        setOpen(false);
        if (inputElRef.current) {
            inputElRef.current.blur();
        }
    };

    const _handleClear = () => {
        if (disableClearable) {
            return;
        }
        onChange(null);
        if (onInputChange) {
            onInputChange('');
        }
    };

    return (
        <div className={styles.autocomplete} style={{ width }}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant='outline'
                        role='combobox'
                        aria-expanded={open}
                        className='w-full justify-between hover:bg-transparent hover:text-inherit'
                        disabled={disabled}
                        data-testid='autocomplete-textfield'
                        onClick={() => {
                            if (!open) {
                                setHasUserTypedSinceOpen(false);
                            }
                        }}
                    >
                        <span className={`flex items-center gap-2 w-full overflow-hidden ${selectedOption ? 'text-primary' : ''}`}>
                            {prefixIcon && <span className='shrink-0'>{prefixIcon}</span>}
                            <span
                                className={cn(
                                    'min-w-0 flex-1 truncate text-left',
                                    selectedOption ? 'text-primary' : 'text-muted-foreground'
                                )}
                                title={selectedOption ? String(selectedOption.label ?? selectedOption.value) : placeholder || 'Select...'}
                            >
                                {selectedOption ? String(selectedOption.label ?? selectedOption.value) : placeholder || 'Select...'}
                            </span>
                        </span>
                        <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0'>
                    <Command shouldFilter={false}>
                        <CommandInput
                            ref={inputElRef as React.RefObject<HTMLInputElement>}
                            placeholder={placeholder}
                            value={inputDisplayValue}
                            onValueChange={val => {
                                handleInputChange({ target: { value: val } } as React.ChangeEvent<HTMLInputElement>);
                            }}
                        />
                        <CommandList ref={commandListRef}>
                            <CommandEmpty>No results.</CommandEmpty>
                            <CommandGroup>
                                {filteredOptions.map(opt => {
                                    const isSelected = selectedOption?.value === opt.value;
                                    const isDisabled = (opt as any).isDisabled === true;
                                    return (
                                        <CommandItem
                                            key={opt.value}
                                            value={String(opt.label ?? opt.value)}
                                            disabled={isDisabled}
                                            data-option-value={String(opt.value)}
                                            className={cn(isSelected ? 'bg-accent text-accent-foreground' : '')}
                                            onSelect={() => handleSelect(opt)}
                                            style={customStyle}
                                        >
                                            {selectedOption && (
                                                <Check className={cn('mr-2 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'invisible')} />
                                            )}
                                            {opt.label}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default Autocomplete;
